// SPEC-NEVER-STUCK-AUTH-AND-CHUNK-01 (D-230) Part B
//   原則: 返ってこない相手に必ず時間切れと逃げ道を用意する。動的 import(遅延読込チャンク)が
//   「拒否された」場合と「決着しない」場合の両方に備える。
//     - 拒否(reject)対策 … retryImport: 300ms 空けて 1 回だけ再試行、それでも失敗なら sessionStorage に
//       印を付けて 1 回だけ reload。印がすでにあれば reload せず上へ伝える(無限リロード禁止)。
//     - 決着しない対策 … PageLoader が 10 秒で再読み込みボタンを出す(B3)。App.jsx の Suspense fallback に使う。
import { useState, useEffect } from 'react'
import { Sentry } from './sentry'

// B3: 読込が決着しないとき、この秒数を超えたら抜け道(再読み込みボタン)を出す。
const PAGE_SLOW_MS = 10000

// B1: 「1 回だけ reload 済み」を示す印。ErrorBoundary の 'chunk-reload'(別機構)とは別キーにして
//   互いのクリア/セットが干渉しないようにする。読込成功で消し、次のデプロイに備える。
export const RELOAD_MARK = 'd230-chunk-reloaded'

// B4: chunk 系の計測。トークンや個人情報は送らない(パスと経過のみ)。送信失敗は握りつぶす。
function reportChunk(kind, extra = {}) {
  try {
    Sentry.captureMessage(`chunk ${kind}`, {
      level: 'warning',
      tags: { diag: 'D-230', kind },
      extra: { path: (typeof location !== 'undefined' && location.pathname) || null, ...extra },
    })
  } catch { /* 計測失敗は逃げ道を壊さない */ }
}

// B3(App.jsx PageLoader から使用): 読込が 10 秒以上決着しないときの計測。
export function reportChunkSlow(elapsedMs) {
  reportChunk('chunk-slow', { elapsedMs })
}

function readMark() {
  try { return !!sessionStorage.getItem(RELOAD_MARK) } catch { return false }
}
function setMark() {
  try { sessionStorage.setItem(RELOAD_MARK, '1') } catch { /* private mode 等 */ }
}
function clearMark() {
  try { sessionStorage.removeItem(RELOAD_MARK) } catch { /* private mode 等 */ }
}

/**
 * 動的 import を包み、拒否時に「300ms 待って 1 回再試行 → それでもダメなら 1 回だけ reload」する。
 * 既に reload 済(印あり)なら reload せず、ErrorBoundary の chunk 検出に当たらない名前のエラーを投げる
 * (= EB の自動 reload と二重にならず、FallbackUI へ落ちる)。成功時は印を消す。
 * @param {() => Promise<any>} factory 元の () => import('...')
 * @returns {Promise<any>}
 */
export function retryImport(factory) {
  return factory().then(
    (mod) => { clearMark(); return mod },
    () => {
      // 1 回目失敗 → 300ms 待って再試行
      reportChunk('chunk-retry', { phase: 'retry' })
      return new Promise((resolve) => setTimeout(resolve, 300))
        .then(() => factory())
        .then(
          (mod) => { clearMark(); return mod },
          (err2) => {
            // 2 回目も失敗
            if (!readMark()) {
              // まだ reload していない → 印を付けて 1 回だけ reload
              setMark()
              reportChunk('chunk-retry', { phase: 'reload' })
              try { window.location.reload() } catch { /* ignore */ }
              return new Promise(() => {}) // reload 中は解決も拒否もしない
            }
            // 既に reload 済 → これ以上 reload しない。EB の ChunkLoadError 検出に当たらない名前で投げる。
            const e = new Error('chunk load failed after retry and reload')
            e.name = 'ChunkRetryExhausted'
            e.cause = err2
            throw e
          }
        )
    }
  )
}

// B3: Suspense の fallback。通常は読込中スピナーのみ。10 秒を超えても決着しない場合(=今回のタナサポの
//   固まり方。Promise が reject されず永久 pending なので B1 では抜けられない)は、時間がかかっている旨と
//   再読み込みボタンを出してスタッフが自力で復帰できるようにする。B4: 抜け道表示時に 1 件計測する。
export function PageLoader() {
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    const id = setTimeout(() => {
      setSlow(true)
      reportChunkSlow(PAGE_SLOW_MS)
    }, PAGE_SLOW_MS)
    return () => clearTimeout(id)
  }, [])
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto mb-3" />
        <p className="text-muted text-sm">読み込み中...</p>
        {slow && (
          <div className="mt-4">
            <p className="text-muted text-xs mb-2">読み込みに時間がかかっています。</p>
            <button
              type="button"
              data-testid="page-loader-reload"
              onClick={() => { try { window.location.reload() } catch { /* ignore */ } }}
              className="px-4 py-2 rounded-lg border border-accent text-accent text-sm active:scale-95"
            >
              再読み込み
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
